import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_SIM_APNS,
  useManagementCreateSimApn,
  useManagementDeleteSimApn,
  useManagementGetSimApn,
  useManagementUpdateSimApn
} from '@apps-common/apis/management/sim-apn'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalContainer, ModalFooter, ModalHeader } from '@apps-common/components/modals/modal'
import { ModalHeaderContainer } from '@apps-common/components/modals/modal/ModalHeaderContainer'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { queryClient } from '@apps-common/lib'
import { ISimApnModel } from '@apps-common/types/management/sim-apn'

export const SimApnModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { simApnId: string }
}) => {
  const {
    isLoading,
    isError,
    data: apn
  } = useManagementGetSimApn({
    apnId: componentProps?.simApnId || '',
    config: {
      enabled: !!componentProps?.simApnId
    }
  })

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<ISimApnModel>({})

  const onChanged = () => {
    queryClient.refetchQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_SIM_APNS() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createSimApn } = useManagementCreateSimApn(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateSimApn } = useManagementUpdateSimApn(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteSimApn } = useManagementDeleteSimApn(true, () => onChanged())

  useEffect(() => {
    if (apn) {
      reset(apn)
    }
  }, [apn])

  const onSubmit = async (saData: any) => {
    try {
      let res

      if (saData.simApnId) {
        updateSimApn({ model: getValues() as any })
      } else {
        createSimApn({ model: getValues() as any })
      }
    } catch (e: any) {
      // ignore let mutation handle error
    }
  }

  const isDisabled = (componentProps?.simApnId && isLoading) || createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-[0px] !h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.simApnId ? 'Updating' : 'Creating'} title='Sim APN' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.simApnId && (
              <ModalButton
                component={ConfirmModal}
                classes=''
                disabled={false}
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this apn. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteSimApn({ apnId: componentProps?.simApnId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>

      <ModalBody classes='relative !pt-[110px] !pb-[80px]'>
        {!isLoading && isError && <p className='mb-[10px] text-error text-sm'>APN failed to load. Please close window and try again.</p>}
        <form noValidate id='simApnModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputSM
              type='text'
              placeholder='Name of APN'
              label='APN name'
              id='unitPlanName'
              showLabel
              disabled={isDisabled}
              registration={register('simApnName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.simApnName}
            />
            <CheckboxInputSM
              label='Is the default APN'
              id='defaultItem'
              disabled={isDisabled}
              registration={register('defaultItem')}
              errors={errors.defaultItem}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='simApnModalForm'
            type='submit'
            loading={isDisabled}
            text={apn ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
